import { useCallback, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { Text } from 'foundations-components/components/Text';
import { Alert } from 'foundations-components/transitional/components/Alert';
import BusinessUnitSelect from 'ui-business-units-lib/components/BusinessUnitSelect';
import WithBusinessUnitsDataFetching from 'ui-business-units-lib/utils/WithBusinessUnitsDataFetching';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { useFetchResolver } from 'reference-resolvers-lite/ResolverHooks';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { BUSINESS_UNIT } from 'customer-data-objects/externalOptions/ExternalOptionsReferenceTypes';
import { updateDocumentBrand } from '../../api/DocumentsApi';
import { addAlert } from 'documents-lib/js/utils/addAlert';
import { updateDocumentBrandSuccess } from '../../actions/DocumentsActions';
import { trackInteraction } from 'documents-lib/js/tracking/tracker';
import { SAVE_CHANGE_BRAND } from 'documents-lib/js/tracking/actions';
import { UsageTrackingScreen } from 'documents-ui/constants/UsageTracking';
import { updateDocumentBrandInIndexFromMetadata } from 'documents-ui/utils/updateDocumentBrandInIndex';
import { ACCOUNT_BRAND_ID } from 'documents-ui/constants/BrandConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AccountBrandSelect = WithBusinessUnitsDataFetching(BusinessUnitSelect);
const ChangeBrandModal = ({
  documentMetadata,
  onSave,
  onReject
}) => {
  const documentId = documentMetadata.get('deckId') || ACCOUNT_BRAND_ID;
  const currentBrandId = documentMetadata.get('brandId');
  const numShares = documentMetadata.get('numShares') || 0;
  const dispatch = useDispatch();
  const [selectedBrandId, setSelectedBrandId] = useState(currentBrandId);
  const [saving, setSaving] = useState(false);

  // Use Reference Resolvers Lite to get the name of the currently selected brand for the alert message
  const resolver = useMemo(() => createStandardCachedResolver(BUSINESS_UNIT), []);
  const {
    reference
  } = useFetchResolver(resolver, String(selectedBrandId));
  const brandName = (reference === null || reference === void 0 ? void 0 : reference.label) || String(selectedBrandId);
  const hasBrandChanged = selectedBrandId !== currentBrandId;
  const handleSave = useCallback(() => {
    setSaving(true);
    updateDocumentBrand({
      documentId,
      brandId: selectedBrandId
    }).then(() => {
      setSaving(false);
      addAlert('updateDocumentBrand', 'success', {
        brandName
      });
      dispatch(updateDocumentBrandSuccess(documentId, selectedBrandId));
      updateDocumentBrandInIndexFromMetadata(documentMetadata, selectedBrandId);
      trackInteraction(SAVE_CHANGE_BRAND, UsageTrackingScreen.DETAIL);
      onSave();
    }).catch(() => {
      setSaving(false);
      addAlert('updateDocumentBrand', 'danger', {});
    });
  }, [documentId, documentMetadata, selectedBrandId, brandName, dispatch, onSave]);
  return /*#__PURE__*/_jsxs(Modal, {
    size: "auto",
    width: 500,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onReject
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "summary.header.buttons.changeBrand"
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [numShares > 0 && /*#__PURE__*/_jsx(Alert, {
        type: "info",
        marginBlockEnd: "400",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "changeBrandModal.alert_jsx",
          options: {
            numShares,
            count: numShares
          },
          elements: {
            strong: 'strong'
          }
        })
      }), /*#__PURE__*/_jsx(Text, {
        as: "label",
        marginBlockEnd: "100",
        display: "block",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "summary.brand.label"
        })
      }), /*#__PURE__*/_jsx(AccountBrandSelect, {
        buId: selectedBrandId,
        onChange: id => {
          if (id !== undefined) {
            setSelectedBrandId(id);
          }
        }
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: handleSave,
        disabled: saving || !hasBrandChanged,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "changeBrandModal.save"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "tertiary-light",
        onClick: onReject,
        disabled: saving,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "changeBrandModal.cancel"
        })
      })]
    })]
  });
};
export default WithBusinessUnitsDataFetching(ChangeBrandModal);